import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { 
  ArrowLeft,
  MessageSquare, 
  Search, 
  Send,
  Phone,
  Mail,
  RefreshCw,
  CheckCircle,
  Archive,
  RotateCcw,
  User,
  Image as ImageIcon,
  Video,
  Paperclip,
  MessageCircle,
  X,
  UserPlus,
  History,
  Tags
} from 'lucide-react'
import type { RootState } from '../store'
import { Conversation, Message, FinishInfo, TransferHistoryItem, type ContactTag } from '../types/conversation.types'
import { tagService } from '../services/tagService'
import { ConversationMedia } from '../types/media.types'
import { conversationService } from '../services/conversationService'
import { userService } from '../services/userService'
import { botService } from '../services/botService'
import { mediaService } from '../services/mediaService'
import { imageService } from '../services/imageService'
import { videoService } from '../services/videoService'
import { documentService } from '../services/documentService'
import { Loading } from '../components/common/Loading'
import { ConfirmActionModal } from '../components/common/ConfirmActionModal'
import { AttendanceHistoryModal } from '../components/conversations/AttendanceHistoryModal'
import { useWebSocket } from '../hooks/useWebSocket'
import MediaMessage from '../components/MediaMessage'
import { QuickRepliesPickerModal } from '../components/quick-replies/QuickRepliesPickerModal'
import { QuickRepliesManagerModal } from '../components/quick-replies/QuickRepliesManagerModal'
import { ContactTagsWorkspaceModal } from '../components/conversations/ContactTagsWorkspaceModal'
import { TagFilterPanel } from '../components/common/TagFilterPanel'
import toast from 'react-hot-toast'
import { logger } from '../utils/logger'

/** Alinha à direita (equipe): bot ou atendente humano. O widget externo grava `attendant`. */
function isStaffSenderType(senderType: Message['sender_type'] | string | undefined | null): boolean {
  return senderType === 'bot' || senderType === 'attendant'
}

const Conversations: React.FC = () => {
  const navigate = useNavigate()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [conversationMedia, setConversationMedia] = useState<ConversationMedia[]>([])
  const [bots, setBots] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [selectedBot, setSelectedBot] = useState<number | null>(null)
  const [newMessage, setNewMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [includeFinished, setIncludeFinished] = useState(false)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null)
  const [selectedDocument, setSelectedDocument] = useState<File | null>(null)
  const [assumeModalConversation, setAssumeModalConversation] = useState<Conversation | null>(null)
  const [transferModalOpen, setTransferModalOpen] = useState(false)
  const [attendantsList, setAttendantsList] = useState<{ id: number; name: string; email: string }[]>([])
  const [selectedToAttendantId, setSelectedToAttendantId] = useState<number | null>(null)
  const [isTransferring, setIsTransferring] = useState(false)
  const [transferNote, setTransferNote] = useState('')
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [historyModalData, setHistoryModalData] = useState<{ finish_info: FinishInfo | null; transfer_history: TransferHistoryItem[] } | null>(null)
  const [historyModalLoading, setHistoryModalLoading] = useState(false)
  const [isDragOverComposer, setIsDragOverComposer] = useState(false)
  /** Modal estilo e-mail: mensagem da transferência (exibido apenas 1ª vez ao receber, até marcar como lida). */
  const [transferNoteModal, setTransferNoteModal] = useState<{ conversationId: number; transfer: TransferHistoryItem } | null>(null)
  /** Confirmação antes de finalizar ou arquivar conversa */
  const [conversationActionModal, setConversationActionModal] = useState<'finish' | 'archive' | null>(null)
  const [isConversationActionLoading, setIsConversationActionLoading] = useState(false)

  const { user } = useSelector((state: RootState) => state.auth)
  const { on, off } = useWebSocket()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null)
  const conversationsAbortRef = useRef<AbortController | null>(null)
  const [quickRepliesModalOpen, setQuickRepliesModalOpen] = useState(false)
  const [quickRepliesManagerOpen, setQuickRepliesManagerOpen] = useState(false)
  const [tagsWorkspaceOpen, setTagsWorkspaceOpen] = useState(false)
  const [availableTags, setAvailableTags] = useState<ContactTag[]>([])
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
  const [tagMatch, setTagMatch] = useState<'any' | 'all'>('any')

  const loadConversations = useCallback(async () => {
    // Cancelar requisição anterior para evitar corrida durante digitação rápida
    if (conversationsAbortRef.current) {
      conversationsAbortRef.current.abort()
    }
    const controller = new AbortController()
    conversationsAbortRef.current = controller

    try {
      setIsLoading(true)
      const filters: any = {}
      if (selectedBot) {
        filters.bot_session_id = selectedBot
      }
      if (debouncedSearchTerm) filters.search = debouncedSearchTerm
      if (includeFinished) filters.include_finished = true
      // Na rota /conversations, exibir por padrão apenas conversas aguardando atendimento humano
      if (!includeFinished) {
        filters.status = 'human'
      }

      filters.include_tags = true
      if (selectedTagIds.length > 0) {
        filters.tag_ids = [...selectedTagIds].sort((a, b) => a - b).join(',')
        filters.tag_match = tagMatch
      }

      const result = await conversationService.getConversations(filters, controller.signal)
      if (result.success && result.data?.conversations) {
        setConversations(result.data.conversations)
      }
    } catch (error: any) {
      // Requisição cancelada: ignorar silenciosamente
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        return
      }
      logger.error('Erro ao carregar conversas:', error)
      const code = error?.response?.data?.code
      if (error?.response?.status === 400 && code === 'INVALID_TAG_IDS') {
        toast.error('Uma ou mais tags selecionadas não estão disponíveis para o seu usuário.')
      } else {
        toast.error('Erro ao carregar conversas')
      }
    } finally {
      // Evitar desligar loading por uma requisição antiga já cancelada
      if (conversationsAbortRef.current === controller) {
        setIsLoading(false)
      }
    }
  }, [selectedBot, debouncedSearchTerm, includeFinished, selectedTagIds, tagMatch])

  const loadTagsCatalog = useCallback(async () => {
    try {
      const res = await tagService.listTags()
      if (res.success && Array.isArray(res.data)) {
        setAvailableTags(res.data)
      }
    } catch (e) {
      logger.error('Erro ao carregar tags:', e)
    }
  }, [])

  const loadBots = async () => {
    try {
      try {
        const permittedResult = await botService.getPermittedBots()
        if (permittedResult.success && permittedResult.data && permittedResult.data.length > 0) {
          setBots(permittedResult.data)
          return
        }
      } catch {
        logger.debug('Tentando carregar todos os bots (usuário admin)...')
      }

      const result = await botService.getBots()
      if (result.success && result.data?.bots) {
        setBots(result.data.bots)
      }
    } catch (error) {
      logger.error('Erro ao carregar bots:', error)
    }
  }

  // Função para fazer scroll automático para o final
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const adjustMessageTextareaHeight = useCallback(() => {
    const textarea = messageTextareaRef.current
    if (!textarea) return

    const minHeightWhenEmptyPx = 36
    const minHeightWithContentPx = 42
    const maxHeightPx = 100
    const hasContent = newMessage.trim().length > 0

    textarea.style.height = 'auto'
    const minHeightPx = hasContent ? minHeightWithContentPx : minHeightWhenEmptyPx
    const nextHeight = Math.max(minHeightPx, Math.min(textarea.scrollHeight, maxHeightPx))
    textarea.style.height = `${nextHeight}px`
    textarea.style.overflowY = textarea.scrollHeight > maxHeightPx ? 'auto' : 'hidden'
  }, [newMessage])

  // Função para verificar se está no final do scroll
  const isScrolledToBottom = () => {
    if (!messagesContainerRef.current) return true
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current
    return scrollHeight - scrollTop - clientHeight < 100
  }

  useEffect(() => {
    loadBots()
    loadTagsCatalog()
    loadConversations()
  }, [])

  // Cleanup: cancelar chamada pendente ao desmontar
  useEffect(() => {
    return () => {
      if (conversationsAbortRef.current) {
        conversationsAbortRef.current.abort()
      }
    }
  }, [])

  // Debounce do campo de busca para evitar requisição a cada tecla
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim())
    }, 350)
    return () => clearTimeout(timeoutId)
  }, [searchTerm])

  // Recarregar conversas quando o filtro de conversas finalizadas mudar
  useEffect(() => {
    loadConversations()
  }, [includeFinished, loadConversations])

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id)
    }
  }, [selectedConversation])

  // Ao abrir uma conversa: se existir uma transferência com note para o atendente atual ainda não lida,
  // exibir uma única vez o modal estilo e-mail (De / Para / Data / note). Ao fechar, marcar como lida.
  useEffect(() => {
    const maybeShowTransferNoteOnce = async () => {
      if (!selectedConversation || !user || user.type !== 'user') return

      try {
        const res = await conversationService.getConversation(selectedConversation.id)
        if (!res.success || !res.data || !res.data.transfer_history) return

        const unreadForMe = res.data.transfer_history
          .filter(
            t =>
              t.to_attendant_id === user.id &&
              t.note &&
              t.note.trim() &&
              !t.note_read_at
          )
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

        const mostRecent = unreadForMe[0]
        if (!mostRecent || mostRecent.id == null) return

        setTransferNoteModal({ conversationId: selectedConversation.id, transfer: mostRecent })
      } catch (error) {
        logger.error('Erro ao carregar nota da transferência:', error)
      }
    }

    maybeShowTransferNoteOnce()
  }, [selectedConversation?.id, user?.id])

  // Fazer scroll para o final quando as mensagens carregam
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        scrollToBottom()
      }, 100)
    }
  }, [messages])

  useEffect(() => {
    adjustMessageTextareaHeight()
  }, [newMessage, adjustMessageTextareaHeight])

  // WebSocket listeners
  useEffect(() => {
    const handleNewMessage = (data: { conversationId: number; message: Message; timestamp: string }) => {
      logger.debug('🔔 WebSocket: Nova mensagem recebida:', data)
      
      // Se a mensagem é da conversa selecionada, adicionar à lista
      if (selectedConversation && data.conversationId === selectedConversation.id) {
        setMessages(prev => {
          // Verificar se a mensagem já existe para evitar duplicatas
          const messageExists = prev.some(msg => msg.id === data.message.id)
          if (messageExists) {
            return prev
          }
          const newMessages = [...prev, data.message]
          // Fazer scroll para o final se estiver próximo do final
          setTimeout(() => {
            if (isScrolledToBottom()) {
              scrollToBottom()
            }
          }, 100)
          return newMessages
        })
      }
      
      // Atualizar a lista de conversas para mostrar a nova mensagem
      setConversations(prev => {
        const updatedConversations = prev.map(conv => 
          conv.id === data.conversationId 
            ? { 
                ...conv, 
                last_message: data.message.message_text, 
                started_at: new Date().toISOString(),
                // Mover a conversa para o topo da lista
                updated_at: new Date().toISOString()
              }
            : conv
        )
        
        // Reordenar para colocar a conversa atualizada no topo
        return updatedConversations.sort((a, b) => {
          if (a.id === data.conversationId) return -1
          if (b.id === data.conversationId) return 1
          return new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        })
      })
    }

    on('new-message', handleNewMessage)

    return () => {
      off('new-message', handleNewMessage)
    }
  }, [on, off, selectedConversation])

  // Função para carregar mídia de uma conversa
  const loadConversationMedia = async (conversationId: number, forceRefresh: boolean = false) => {
    try {
      logger.debug(`🔍 Carregando mídia para conversa ${conversationId}, forceRefresh: ${forceRefresh}`)
      const response = await mediaService.getConversationMedia(conversationId, forceRefresh)
      if (response.success) {
        logger.debug(`📎 Mídia carregada:`, response.data)
        logger.debug(`🔍 Detalhes da mídia:`, response.data.map((media: any) => ({
          id: media.id,
          type: media.type,
          message: media.message,
          sender_type: media.message?.sender_type
        })))
        setConversationMedia(response.data)
      } else {
        logger.error('❌ Erro ao carregar mídia', { success: response?.success })
      }
    } catch (error) {
      logger.error('Erro ao carregar mídia da conversa:', error)
    }
  }

  // Listener para nova mídia via WebSocket
  useEffect(() => {
    const handleNewMedia = (data: { conversationId: number; mediaId: number; type: 'audio' | 'image'; fileUrl: string; timestamp: string }) => {
      logger.debug('🔔 WebSocket: Nova mídia recebida:', data)
      logger.debug('🔔 WebSocket: Conversa selecionada:', selectedConversation?.id)
      
      // Se for da conversa atual, recarregar mídia
      if (selectedConversation && data.conversationId === selectedConversation.id) {
        logger.debug('🔄 Recarregando mídia da conversa atual')
        loadConversationMedia(data.conversationId, true) // Force refresh
        // Também recarregar mensagens para mostrar a nova mídia
        loadMessages(data.conversationId)
      }
    }

    on('new-media', handleNewMedia)

    return () => {
      off('new-media', handleNewMedia)
    }
  }, [on, off, selectedConversation])

  // Listener adicional para atualizar conversas quando não há conversa selecionada
  useEffect(() => {
    const handleNewMessageGlobal = (data: { conversationId: number; message: Message; timestamp: string }) => {
      logger.debug('🔔 WebSocket Global: Nova mensagem recebida:', data)
      
      // Sempre atualizar a lista de conversas
      setConversations(prev => {
        // Verificar se a conversa já existe na lista
        const conversationExists = prev.some(conv => conv.id === data.conversationId)
        
        if (conversationExists) {
          // Se existe, atualizar
          const updatedConversations = prev.map(conv => 
            conv.id === data.conversationId 
              ? { 
                  ...conv, 
                  last_message: data.message.message_text, 
                  started_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                }
              : conv
          )
          
          // Reordenar para colocar a conversa atualizada no topo
          return updatedConversations.sort((a, b) => {
            if (a.id === data.conversationId) return -1
            if (b.id === data.conversationId) return 1
            return new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
          })
        } else {
          // Se não existe na lista, recarregar a lista completa
          // (pode ser uma conversa que mudou de status 'bot' para 'human' ou uma nova conversa 'human')
          logger.debug('🔔 Conversa não encontrada na lista, recarregando lista completa...')
          // Chamar loadConversations assincronamente (não pode aguardar dentro do setState)
          setTimeout(() => {
            loadConversations()
          }, 100)
          return prev
        }
      })
    }

    on('new-message', handleNewMessageGlobal)

    return () => {
      off('new-message', handleNewMessageGlobal)
    }
  }, [on, off, loadConversations])

  // Listener para atualizar conversas quando há mudanças no bot
  useEffect(() => {
    const handleBotStatusUpdate = (data: { botId: number; status: string; isConnected: boolean }) => {
      logger.debug('🔔 WebSocket: Status do bot atualizado:', data)
      // Recarregar conversas quando o status do bot muda
      loadConversations()
    }

    on('bot-status-update', handleBotStatusUpdate)

    return () => {
      off('bot-status-update', handleBotStatusUpdate)
    }
  }, [on, off, loadConversations])

  // Listener para quando uma conversa é transferida para atendimento humano
  // Isso acontece quando uma conversa muda de status 'bot' para 'human'
  useEffect(() => {
    const handleConversationTransferred = (data: { conversationId: number; status: string; menu?: string; timestamp?: string }) => {
      logger.debug('🔔 WebSocket: Conversa transferida para humano:', data)
      loadConversations()
    }

    on('conversation-transferred', handleConversationTransferred)

    return () => {
      off('conversation-transferred', handleConversationTransferred)
    }
  }, [on, off, loadConversations])

  // Listener para quando um atendimento é transferido para o usuário atual (atendente recebe a conversa)
  useEffect(() => {
    const handleAttendanceTransferred = (data: { conversationId: number; conversation?: Conversation; timestamp?: string }) => {
      logger.debug('🔔 WebSocket: Atendimento transferido para você:', data)
      loadConversations()
    }

    on('attendance-transferred', handleAttendanceTransferred)

    return () => {
      off('attendance-transferred', handleAttendanceTransferred)
    }
  }, [on, off, loadConversations])

  // Listener para quando uma nova conversa é criada
  useEffect(() => {
    const handleNewConversation = (data: { conversation: Conversation; timestamp?: string }) => {
      logger.debug('🔔 WebSocket: Nova conversa criada:', data)
      // Recarregar lista de conversas para incluir a nova conversa
      loadConversations()
    }

    on('new-conversation', handleNewConversation)

    return () => {
      off('new-conversation', handleNewConversation)
    }
  }, [on, off, loadConversations])

  /** Tag aplicada na conversa (ex.: menu automático) — atualiza lista e detalhe sem refetch completo. */
  useEffect(() => {
    const handleConversationTagsUpdated = (data: {
      conversationId: number
      tag: ContactTag
      timestamp?: string
    }) => {
      logger.debug('🔔 WebSocket: Tags da conversa atualizadas:', data)
      const { conversationId, tag } = data
      if (!tag?.id) return

      const mergeTag = (tags: ContactTag[] | undefined): ContactTag[] => {
        const list = tags ?? []
        if (list.some((t) => t.id === tag.id)) return list
        return [...list, tag]
      }

      setConversations((prev) => {
        const found = prev.some((c) => c.id === conversationId)
        if (!found) {
          setTimeout(() => loadConversations(), 0)
          return prev
        }
        return prev.map((conv) =>
          conv.id === conversationId ? { ...conv, tags: mergeTag(conv.tags) } : conv
        )
      })

      setSelectedConversation((prev) => {
        if (!prev || prev.id !== conversationId) return prev
        return { ...prev, tags: mergeTag(prev.tags) }
      })
    }

    on('conversation-tags-updated', handleConversationTagsUpdated)

    return () => {
      off('conversation-tags-updated', handleConversationTagsUpdated)
    }
  }, [on, off, loadConversations])

  const loadMessages = async (conversationId: number) => {
    try {
      setIsLoadingMessages(true)
      const result = await conversationService.getMessages(conversationId)
      if (result.success && result.data?.messages) {
        setMessages(result.data.messages)
      }
      
      // Carregar mídia da conversa
      await loadConversationMedia(conversationId)
    } catch (error) {
      logger.error('Erro ao carregar mensagens:', error)
      toast.error('Erro ao carregar mensagens')
    } finally {
      setIsLoadingMessages(false)
    }
  }

  const handleSendMessage = async () => {
    if (!selectedConversation || (!newMessage.trim() && !selectedImage && !selectedVideo && !selectedDocument)) return
    if (selectedConversation.status === 'finished' || selectedConversation.status === 'archived') return

    try {
      setIsSending(true)
      
      // Se há um vídeo selecionado, enviar como vídeo + texto
      if (selectedVideo) {
        const result = await videoService.sendVideoToClient(
          selectedConversation.id, 
          selectedVideo, 
          newMessage.trim() || undefined
        )
        
        if (result.success) {
          toast.success('Vídeo enviado com sucesso!')
          setSelectedVideo(null)
          setNewMessage('')
          mediaService.invalidateConversationCache(selectedConversation.id)
          loadMessages(selectedConversation.id)
        } else {
          toast.error(result.message || 'Erro ao enviar vídeo')
        }
      }
      // Se há um documento selecionado, enviar como documento + texto
      else if (selectedDocument) {
        const result = await documentService.sendDocumentToClient(
          selectedConversation.id,
          selectedDocument,
          newMessage.trim() || undefined
        )

        if (result.success) {
          toast.success('Documento enviado com sucesso!')
          setSelectedDocument(null)
          setNewMessage('')
          mediaService.invalidateConversationCache(selectedConversation.id)
          loadMessages(selectedConversation.id)
        } else {
          toast.error(result.message || 'Erro ao enviar documento')
        }
      }
      // Se há uma imagem selecionada, enviar como imagem + texto
      else if (selectedImage) {
        const result = await imageService.sendImageToClient(
          selectedConversation.id, 
          selectedImage, 
          newMessage.trim() || undefined
        )
        
        if (result.success) {
          toast.success('Imagem enviada com sucesso!')
          setSelectedImage(null)
          setNewMessage('')
          mediaService.invalidateConversationCache(selectedConversation.id)
          loadMessages(selectedConversation.id)
        } else {
          toast.error(result.message || 'Erro ao enviar imagem')
        }
      } else {
        // Envio normal de mensagem de texto
        const result = await conversationService.sendMessage(
          selectedConversation.id, 
          newMessage.trim()
        )
        
        if (result.success && result.data) {
          setMessages(prev => {
            // Verificar se a mensagem já existe para evitar duplicatas
            const messageExists = prev.some(msg => msg.id === result.data.id)
            if (messageExists) {
              return prev
            }
            const newMessages = [...prev, result.data]
            // Fazer scroll para o final após enviar mensagem
            setTimeout(() => {
              scrollToBottom()
            }, 100)
            return newMessages
          })
          setNewMessage('')
          toast.success('Mensagem enviada!')
        } else {
          toast.error('Erro ao enviar mensagem')
        }
      }
    } catch (error) {
      logger.error('Erro ao enviar mensagem:', error)
      toast.error('Erro ao enviar mensagem')
    } finally {
      setIsSending(false)
    }
  }

  const handleImageSelect = (file: File) => {
    setSelectedImage(file)
    // Limpar vídeo se houver
    if (selectedVideo) {
      setSelectedVideo(null)
    }
    // Não abrir modal, apenas selecionar a imagem
    // O usuário pode digitar texto e enviar normalmente
  }

  const handleVideoSelect = (file: File) => {
    // Validar tamanho (máximo 100MB)
    const maxSize = 100 * 1024 * 1024 // 100MB
    if (file.size > maxSize) {
      toast.error('Vídeo muito grande. Tamanho máximo: 100MB')
      return
    }
    
    // Validar tipo
    if (!file.type.startsWith('video/')) {
      toast.error('Arquivo deve ser um vídeo')
      return
    }
    
    setSelectedVideo(file)
    // Limpar imagem se houver
    if (selectedImage) {
      setSelectedImage(null)
    }
    if (selectedDocument) {
      setSelectedDocument(null)
    }
  }

  const handleDocumentSelect = (file: File) => {
    // Validar tamanho (máximo 20MB para documentos)
    const maxSize = 20 * 1024 * 1024 // 20MB
    if (file.size > maxSize) {
      toast.error('Documento muito grande. Tamanho máximo: 20MB')
      return
    }

    setSelectedDocument(file)
    // Limpar outras mídias para manter envio único por vez
    if (selectedImage) {
      setSelectedImage(null)
    }
    if (selectedVideo) {
      setSelectedVideo(null)
    }
  }

  const handleDroppedFile = (file: File) => {
    if (file.type.startsWith('image/')) {
      handleImageSelect(file)
      return
    }

    if (file.type.startsWith('video/')) {
      handleVideoSelect(file)
      return
    }

    const documentMimeTypes = [
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ]

    const fileName = file.name.toLowerCase()
    const isAcceptedDocumentByExtension =
      fileName.endsWith('.pdf') ||
      fileName.endsWith('.doc') ||
      fileName.endsWith('.docx') ||
      fileName.endsWith('.xls') ||
      fileName.endsWith('.xlsx') ||
      fileName.endsWith('.ppt') ||
      fileName.endsWith('.pptx') ||
      fileName.endsWith('.txt')

    if (documentMimeTypes.includes(file.type) || isAcceptedDocumentByExtension) {
      handleDocumentSelect(file)
      return
    }

    toast.error('Arquivo não suportado. Envie imagem, vídeo ou documento válido.')
  }

  const handleComposerDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isSending) {
      setIsDragOverComposer(true)
    }
  }

  const handleComposerDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOverComposer(false)
  }

  const handleComposerDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOverComposer(false)

    if (isSending) return
    if (selectedConversation?.status === 'finished' || selectedConversation?.status === 'archived') return

    const droppedFiles = e.dataTransfer.files
    if (!droppedFiles || droppedFiles.length === 0) return

    handleDroppedFile(droppedFiles[0])
  }

  /**
   * Reseta o painel da conversa para o estado inicial: fecha a conversa selecionada
   * e exibe "Selecione uma conversa" / "Escolha uma conversa da lista para começar a conversar".
   * Usado após finalizar ou arquivar atendimento humano.
   */
  const resetToInitialConversationState = useCallback(() => {
    setSelectedConversation(null)
    setMessages([])
    setConversationMedia([])
    setSelectedImage(null)
    setSelectedVideo(null)
    setSelectedDocument(null)
    loadConversations()
  }, [loadConversations])

  const handleConfirmFinishOrArchive = async () => {
    if (!selectedConversation || !conversationActionModal) return

    setIsConversationActionLoading(true)
    try {
      if (conversationActionModal === 'finish') {
        const result = await conversationService.finishConversation(selectedConversation.id)
        if (result.success) {
          toast.success('Conversa finalizada com sucesso')
          setConversationActionModal(null)
          resetToInitialConversationState()
        } else {
          toast.error(result.message || 'Erro ao finalizar conversa')
        }
      } else {
        const result = await conversationService.archiveConversation(selectedConversation.id)
        if (result.success) {
          toast.success('Conversa arquivada com sucesso')
          setConversationActionModal(null)
          resetToInitialConversationState()
        } else {
          toast.error(result.message || 'Erro ao arquivar conversa')
        }
      }
    } catch (error) {
      logger.error('Erro ao finalizar ou arquivar conversa:', error)
      toast.error(conversationActionModal === 'finish' ? 'Erro ao finalizar conversa' : 'Erro ao arquivar conversa')
    } finally {
      setIsConversationActionLoading(false)
    }
  }

  const handleAssumeConfirm = async () => {
    if (!assumeModalConversation) return
    try {
      const result = await conversationService.assumeConversation(assumeModalConversation.id)
      if (result.success && result.data) {
        toast.success('Conversa assumida com sucesso!')
        setSelectedConversation(result.data)
        setAssumeModalConversation(null)
        loadConversations()
        loadMessages(result.data.id)
      } else {
        toast.error(result.message || 'Erro ao assumir conversa')
      }
    } catch (error: any) {
      logger.error('Erro ao assumir conversa:', error)
      toast.error(error.response?.data?.message || 'Erro ao assumir conversa')
    }
  }

  const handleOpenTransferModal = useCallback(async () => {
    setTransferModalOpen(true)
    setSelectedToAttendantId(null)
    setTransferNote('')
    try {
      const { users } = await userService.getUsers(1, 100)
      setAttendantsList(users?.map((u: { id: number; name: string; email: string }) => ({ id: u.id, name: u.name, email: u.email })) || [])
    } catch (e) {
      toast.error('Erro ao carregar lista de atendentes')
      setTransferModalOpen(false)
    }
  }, [])

  const handleTransferConfirm = async () => {
    if (!selectedConversation || selectedToAttendantId == null) return
    setIsTransferring(true)
    try {
      const result = await conversationService.transferConversation(
        selectedConversation.id,
        selectedToAttendantId,
        transferNote
      )
      if (result.success && result.data) {
        toast.success('Atendimento transferido com sucesso')
        setSelectedConversation(result.data)
        setTransferModalOpen(false)
        setSelectedToAttendantId(null)
        setTransferNote('')
        loadConversations()
        if (result.data.attendant_id !== user?.id && user?.type === 'user') {
          resetToInitialConversationState()
        }
      } else {
        toast.error(result.message || 'Erro ao transferir')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao transferir')
    } finally {
      setIsTransferring(false)
    }
  }

  const handleTagsApplied = useCallback(async () => {
    await loadTagsCatalog()
    const convId = selectedConversation?.id
    await loadConversations()
    if (convId != null) {
      try {
        const res = await conversationService.getConversation(convId, { include_tags: true })
        if (res.success && res.data) {
          setSelectedConversation(res.data)
        }
      } catch (e) {
        logger.error('Erro ao atualizar conversa após tags:', e)
      }
    }
  }, [loadConversations, loadTagsCatalog, selectedConversation?.id])

  const handleOpenConversationFromTagExplorer = useCallback(
    async (conversationId: number) => {
      const found = conversations.find(c => c.id === conversationId)
      if (found) {
        setSelectedConversation(found)
        return
      }
      try {
        const res = await conversationService.getConversation(conversationId, { include_tags: true })
        if (res.success && res.data) {
          setSelectedConversation(res.data)
          loadConversations()
        }
      } catch (e) {
        logger.error('Abrir conversa a partir do explorador de tags:', e)
        toast.error('Não foi possível abrir a conversa')
      }
    },
    [conversations, loadConversations]
  )

  const canManageTags = user?.type === 'admin' || user?.type === 'user'

  const handleOpenHistoryModal = async () => {
    if (!selectedConversation) return
    setHistoryModalOpen(true)
    setHistoryModalLoading(true)
    setHistoryModalData(null)
    try {
      const res = await conversationService.getConversation(selectedConversation.id)
      if (res.success && res.data) {
        setHistoryModalData({
          finish_info: res.data.finish_info ?? null,
          transfer_history: res.data.transfer_history ?? []
        })
      } else {
        toast.error('Não foi possível carregar o histórico')
        setHistoryModalData({ finish_info: null, transfer_history: [] })
      }
    } catch (e) {
      toast.error('Erro ao carregar histórico')
      setHistoryModalData({ finish_info: null, transfer_history: [] })
    } finally {
      setHistoryModalLoading(false)
    }
  }

  const handleResumeConversation = async () => {
    if (!selectedConversation) return

    try {
      const result = await conversationService.resumeConversation(selectedConversation.id)
      if (result.success) {
        toast.success('Conversa retomada com sucesso')
        loadConversations()
        if (selectedConversation) {
          loadMessages(selectedConversation.id)
        }
      } else {
        toast.error(result.message || 'Erro ao retomar conversa')
      }
    } catch (error) {
      logger.error('Erro ao retomar conversa:', error)
      toast.error('Erro ao retomar conversa')
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Data não disponível'
    
    const date = new Date(dateString)
    
    // Verificar se a data é válida
    if (isNaN(date.getTime())) {
      return 'Data inválida'
    }
    
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    
    if (days === 0) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    } else if (days === 1) {
      return 'Ontem'
    } else if (days < 7) {
      return date.toLocaleDateString('pt-BR', { weekday: 'short' })
    } else {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'closed': return 'bg-gray-100 text-gray-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Ativa'
      case 'closed': return 'Fechada'
      case 'pending': return 'Pendente'
      default: return status
    }
  }

  if (isLoading && conversations.length === 0) {
    return <Loading />
  }

  return (
    <div className="flex h-[100dvh] bg-gray-50 overflow-hidden lg:flex-row flex-col">
      {/* Sidebar - Lista de Conversas */}
      <div className={`bg-white border-r border-gray-200 flex flex-col h-full w-full md:w-2/5 lg:w-1/3 xl:w-[30%] ${
        selectedConversation ? 'hidden lg:flex' : 'flex'
      }`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200 sticky top-0 z-10 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
          <div className="flex items-center justify-between mb-4">
          <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-200 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-2">
              <h1 className="text-xl font-semibold text-gray-900">Conversas</h1>
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                {conversations.length} {includeFinished ? 'total' : 'ativas'}
              </span>
            </div>
            <button
              onClick={loadConversations}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Filtros */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar conversas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={selectedBot || ''}
              onChange={(e) => setSelectedBot(Number(e.target.value) || null)}
              className="w-full px-3 py-2.5 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos os bots</option>
              {bots.map(bot => (
                <option key={bot.id} value={bot.id}>
                  {bot.session_name} ({bot.status})
                </option>
              ))}
            </select>

            {/* Toggle para incluir conversas finalizadas */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="includeFinished"
                checked={includeFinished}
                onChange={(e) => setIncludeFinished(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="includeFinished" className="text-sm text-gray-700">
                Incluir conversas finalizadas
              </label>
            </div>

            <TagFilterPanel
              availableTags={availableTags}
              selectedTagIds={selectedTagIds}
              onChangeSelectedTagIds={setSelectedTagIds}
              tagMatch={tagMatch}
              onChangeTagMatch={setTagMatch}
              usageItems={conversations}
              radioGroupName="tagMatchConversations"
            />
          </div>
        </div>

        {/* Lista de Conversas */}
        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>
                {includeFinished 
                  ? 'Nenhuma conversa encontrada' 
                  : 'Nenhuma conversa ativa encontrada'
                }
              </p>
              {!includeFinished && (
                <p className="text-sm text-gray-400 mt-1">
                  Marque "Incluir conversas finalizadas" para ver todas
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2 p-2 md:p-2.5">
              {conversations.map(conversation => {
                // Conversa sem atendente: bot, waiting_input ou human (fila humana ainda não assumida)
                const isUnassigned = (conversation.status === 'bot' || conversation.status === 'waiting_input' || conversation.status === 'human') && conversation.attendant_id == null
                const isMine = user?.type === 'user' && conversation.attendant_id === user?.id
                const showAssumeModal = user?.type === 'user' && isUnassigned && !includeFinished
                const isReadOnlyConversation = includeFinished && (conversation.status === 'finished' || conversation.status === 'archived')
                return (
                <div
                  key={conversation.id}
                  onClick={() => {
                    if (showAssumeModal) {
                      setAssumeModalConversation(conversation)
                    } else {
                      setSelectedConversation(conversation)
                    }
                  }}
                  className={`p-3.5 md:p-3 rounded-xl cursor-pointer transition-colors ${
                    selectedConversation?.id === conversation.id
                      ? 'bg-blue-50 border-l-4 border-blue-500'
                      : isReadOnlyConversation
                        ? 'bg-gray-50 hover:bg-gray-100 border border-gray-100'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <div className="w-11 h-11 md:w-10 md:h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm md:text-base">
                          {conversation.client?.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-[15px] md:text-sm text-gray-900 truncate">
                            {conversation.client?.name || 'Cliente sem nome'}
                          </h3>
                          <p className="text-[13px] md:text-sm text-gray-500 truncate">
                            {conversation.client?.phone_number || 'N/A'}
                          </p>
                          {conversation.tags && conversation.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {conversation.tags.map(t => (
                                <span
                                  key={t.id}
                                  className="text-[10px] leading-tight px-1.5 py-0.5 rounded border border-gray-200 bg-white/90 text-gray-700 truncate max-w-[100px]"
                                  style={{ borderColor: t.color }}
                                  title={t.name}
                                >
                                  {t.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {conversation.messages && conversation.messages.length > 0 && (
                        <p className="text-[13px] md:text-sm text-gray-600 mt-1.5 truncate">
                          {conversation.messages[conversation.messages.length - 1].message_text}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex flex-col items-end space-y-1">
                      <span className="text-xs text-gray-500">
                        {formatDate(conversation.started_at)}
                      </span>
                      <div className={`flex items-center space-x-2 ${isReadOnlyConversation ? 'opacity-70' : ''}`}>
                        {isReadOnlyConversation && (
                          <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                            Somente leitura
                          </span>
                        )}
                        {isUnassigned && (
                          <div className="flex items-center space-x-1 text-blue-600">
                            <User className="w-3 h-3" />
                            <span className="text-xs font-medium">Assumir</span>
                          </div>
                        )}
                        {conversation.attendant_id != null && !isMine && (
                          <span className="text-xs text-gray-500">Com outro atendente</span>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(conversation.status)}`}>
                          {getStatusText(conversation.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal: Assumir atendimento */}
      {assumeModalConversation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setAssumeModalConversation(null)}>
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Assumir este atendimento?</h3>
            <p className="text-gray-600 text-sm mb-4">
              A conversa com <strong>{assumeModalConversation.client?.name || 'o cliente'}</strong> ficará atribuída a você até finalizar ou transferir.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setAssumeModalConversation(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleAssumeConfirm}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Aceitar
              </button>
            </div>
          </div>
        </div>
      )}

      {conversationActionModal && (
        <ConfirmActionModal
          isOpen
          onClose={() => {
            if (!isConversationActionLoading) setConversationActionModal(null)
          }}
          onConfirm={handleConfirmFinishOrArchive}
          title={conversationActionModal === 'archive' ? 'Arquivar conversa' : 'Finalizar conversa'}
          message={
            conversationActionModal === 'archive' ? (
              <>
                A conversa será <strong>arquivada</strong> e deixará a fila ativa, permanecendo disponível para consulta quando necessário.
                Deseja confirmar?
              </>
            ) : (
              <>
                A conversa será <strong>finalizada</strong> e sairá do atendimento humano em andamento. Você poderá localizá-la depois nas conversas finalizadas, se estiver habilitado o filtro.
                Deseja confirmar?
              </>
            )
          }
          confirmLabel={conversationActionModal === 'archive' ? 'Arquivar' : 'Finalizar'}
          variant={conversationActionModal === 'archive' ? 'warning' : 'success'}
          icon={conversationActionModal === 'archive' ? Archive : CheckCircle}
          isLoading={isConversationActionLoading}
        />
      )}

      {/* Modal: Transferir atendimento */}
      {transferModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => !isTransferring && setTransferModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Transferir atendimento</h3>
            <p className="text-gray-600 text-sm mb-4">
              Selecione o atendente e, se quiser, deixe uma mensagem para quem vai assumir o atendimento.
            </p>
            <select
              value={selectedToAttendantId ?? ''}
              onChange={e => setSelectedToAttendantId(Number(e.target.value) || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
            >
              <option value="">Selecione...</option>
              {attendantsList
                .filter(a => a.id !== user?.id)
                .map(a => (
                  <option key={a.id} value={a.id}>{a.name} ({a.email})</option>
                ))}
            </select>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mensagem para o próximo atendente (opcional)
              </label>
              <textarea
                value={transferNote}
                onChange={e => setTransferNote(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex.: Resumo rápido do caso, pendências, combinações com o cliente..."
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                disabled={isTransferring}
                onClick={() => setTransferModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={!selectedToAttendantId || isTransferring}
                onClick={handleTransferConfirm}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTransferring ? 'Transferindo...' : 'Transferir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Mensagem da transferência (estilo e-mail, apenas 1ª vez) */}
      {transferNoteModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => {
            const { conversationId, transfer } = transferNoteModal
            if (transfer.id != null) {
              conversationService.markTransferNoteRead(conversationId, transfer.id).catch(() => {})
            }
            setTransferNoteModal(null)
          }}
        >
          <div
            className="bg-white rounded-xl shadow-lg p-6 max-w-lg w-full mx-4 max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Mensagem da transferência</h3>
            <div className="space-y-3 text-sm">
              <div>
                <span className="font-medium text-gray-600">De:</span>
                <span className="ml-2 text-gray-900">{transferNoteModal.transfer.from_name ?? '—'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Para:</span>
                <span className="ml-2 text-gray-900">{transferNoteModal.transfer.to_name ?? '—'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Data:</span>
                <span className="ml-2 text-gray-900">
                  {transferNoteModal.transfer.created_at
                    ? new Date(transferNoteModal.transfer.created_at).toLocaleString('pt-BR')
                    : '—'}
                </span>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <span className="font-medium text-gray-600 block mb-1">Mensagem:</span>
                <p className="text-gray-900 whitespace-pre-wrap break-words">
                  {transferNoteModal.transfer.note && transferNoteModal.transfer.note.trim()
                    ? transferNoteModal.transfer.note
                    : '—'}
                </p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  const { conversationId, transfer } = transferNoteModal
                  if (transfer.id != null) {
                    conversationService.markTransferNoteRead(conversationId, transfer.id).catch(() => {})
                  }
                  setTransferNoteModal(null)
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      <AttendanceHistoryModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        loading={historyModalLoading}
        finishInfo={historyModalData?.finish_info ?? null}
        transferHistory={historyModalData?.transfer_history ?? []}
      />

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col h-full min-w-0 ${selectedConversation ? 'flex' : 'hidden lg:flex'}`}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-gray-200 p-3 md:p-4">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"
                    title="Voltar para conversas"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                    {selectedConversation.client?.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-semibold text-gray-900 truncate">
                      {selectedConversation.client?.name || 'Cliente sem nome'}
                    </h2>
                    <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm text-gray-500 flex-wrap">
                      <div className="flex items-center space-x-1">
                        <Phone className="w-3 h-3" />
                        <span className="truncate">{selectedConversation.client?.phone_number || 'N/A'}</span>
                      </div>
                      {selectedConversation.client?.email && (
                        <div className="hidden md:flex items-center space-x-1 min-w-0">
                          <Mail className="w-3 h-3" />
                          <span className="truncate">{selectedConversation.client.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                </div>

                <div className={`flex items-center gap-2 overflow-x-auto pb-1 ${
                  (selectedConversation.status === 'finished' || selectedConversation.status === 'archived') ? 'opacity-70' : ''
                }`}>
                  <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(selectedConversation.status)}`}>
                    {getStatusText(selectedConversation.status)}
                  </span>
                  
                  {/* Botões de ação baseados no status */}
                  {(selectedConversation.status === 'bot' || selectedConversation.status === 'waiting_input') && selectedConversation.attendant_id == null && user?.type === 'user' && (
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <button
                        onClick={async () => {
                          const result = await conversationService.assumeConversation(selectedConversation.id)
                          if (result.success && result.data) {
                            setSelectedConversation(result.data)
                            loadConversations()
                            loadMessages(selectedConversation.id)
                            toast.success('Conversa assumida com sucesso!')
                          } else {
                            toast.error(result.message || 'Erro ao assumir')
                          }
                        }}
                        className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                        title="Assumir conversa (intervenção humana)"
                      >
                        <User className="w-4 h-4" />
                        <span>Atender</span>
                      </button>
                    </div>
                  )}

                  {selectedConversation.status === 'human' && (
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      {(selectedConversation.attendant_id === user?.id || user?.type === 'admin') && (
                        <>
                          <button
                            type="button"
                            onClick={handleOpenHistoryModal}
                            disabled={historyModalLoading}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                            title="Histórico de transferências e finalização"
                          >
                            <History className="w-4 h-4" />
                          </button>
                          <button
                            onClick={handleOpenTransferModal}
                            className="p-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg"
                            title="Transferir atendimento"
                          >
                            <UserPlus className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {canManageTags && (
                        <button
                          type="button"
                          onClick={() => setTagsWorkspaceOpen(true)}
                          className="p-2 text-amber-700 hover:text-amber-800 hover:bg-amber-50 rounded-lg"
                          title="Tags da conversa e do contato"
                        >
                          <Tags className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setConversationActionModal('finish')}
                        className="p-2 text-green-600 hover:text-green-700 hover:bg-green-100 rounded-lg"
                        title="Finalizar conversa"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      {user?.type === 'admin' && (
                        <button
                          type="button"
                          onClick={() => setConversationActionModal('archive')}
                          className="p-2 text-orange-600 hover:text-orange-700 hover:bg-orange-100 rounded-lg"
                          title="Arquivar conversa"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                  
                  {(selectedConversation.status === 'finished' || selectedConversation.status === 'archived') && (
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <button
                        onClick={handleOpenHistoryModal}
                        disabled={historyModalLoading}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                        title="Ver quem finalizou e histórico de transferências"
                      >
                        <History className="w-4 h-4" />
                      </button>
                      {canManageTags && (
                        <button
                          type="button"
                          onClick={() => setTagsWorkspaceOpen(true)}
                          className="p-2 text-amber-700 hover:text-amber-800 hover:bg-amber-50 rounded-lg"
                          title="Tags da conversa e do contato"
                        >
                          <Tags className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={handleResumeConversation}
                        className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded-lg"
                        title="Retomar conversa"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  
                  {(selectedConversation.status === 'bot' || selectedConversation.status === 'waiting_input') && (
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      {selectedConversation.status === 'bot' && (
                        <span className="text-xs text-gray-500 flex items-center">
                          <User className="w-3 h-3 mr-1" />
                          Bot ativo
                        </span>
                      )}
                      {canManageTags && (
                        <button
                          type="button"
                          onClick={() => setTagsWorkspaceOpen(true)}
                          className="p-2 text-amber-700 hover:text-amber-800 hover:bg-amber-50 rounded-lg"
                          title="Tags da conversa e do contato"
                        >
                          <Tags className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4"
            >
              {isLoadingMessages ? (
                <div className="flex justify-center items-center h-32">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>Nenhuma mensagem nesta conversa</p>
                </div>
              ) : (
                <>
                  {(() => {
                    // Criar lista unificada de mensagens e mídia
                    const allItems = [
                      ...messages.map(msg => ({ ...msg, itemType: 'message' as const })),
                      ...conversationMedia.map(media => ({ ...media, itemType: 'media' as const, sent_at: media.timestamp }))
                    ].sort((a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime());

                    logger.debug(`🔍 Renderizando chat - Mensagens: ${messages.length}, Mídia: ${conversationMedia.length}, Total: ${allItems.length}`)
                    logger.debug(`📎 Mídia disponível:`, conversationMedia)

                    return allItems.map((item, index) => {
                      if (item.itemType === 'message') {
                        logger.debug(`🔍 Processando mensagem:`, {
                          id: item.id,
                          message_type: item.message_type,
                          message_text: item.message_text,
                          sender_type: item.sender_type
                        });
                        
                        // Verificar se é uma mensagem de mídia (imagem, áudio, vídeo ou documento)
                        if (
                          item.message_type === 'image' ||
                          item.message_type === 'audio' ||
                          item.message_type === 'video' ||
                          item.message_type === 'document'
                        ) {
                          // Não renderizar como texto; será exibido apenas no bloco de mídia
                          return null;
                        }

                        // Mensagem de localização: exibir apenas latitude e longitude (atendente copia e verifica no Google Maps)
                        if (item.message_type === 'location') {
                          const meta = (item as Message).metadata;
                          const lat = meta?.latitude;
                          const lng = meta?.longitude;
                          const locationText = typeof lat === 'number' && typeof lng === 'number'
                            ? `${lat}, ${lng}`
                            : item.message_text;
                          return (
                            <div
                              key={`${item.id}-${item.sent_at}-${index}`}
                              className={`flex ${isStaffSenderType(item.sender_type) ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                                  isStaffSenderType(item.sender_type)
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-200 text-gray-900'
                                }`}
                              >
                                <p className="text-xs font-medium opacity-90">Localização</p>
                                <p className="text-sm font-mono mt-1 select-text" title="Copie e cole no Google Maps">
                                  {locationText}
                                </p>
                                <p className={`text-xs mt-1 ${
                                  isStaffSenderType(item.sender_type) ? 'text-blue-100' : 'text-gray-500'
                                }`}>
                                  {new Date(item.sent_at).toLocaleTimeString('pt-BR', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>
                          );
                        }
                        
                        return (
                          <div
                            key={`${item.id}-${item.sent_at}-${index}`}
                            className={`flex ${isStaffSenderType(item.sender_type) ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                                isStaffSenderType(item.sender_type)
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-gray-200 text-gray-900'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap break-words">{item.message_text}</p>
                              <p className={`text-xs mt-1 ${
                                isStaffSenderType(item.sender_type) ? 'text-blue-100' : 'text-gray-500'
                              }`}>
                                {new Date(item.sent_at).toLocaleTimeString('pt-BR', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                        );
                      } else {
                        // Determinar se a mídia é do cliente baseado no sender_type da mensagem
                        const isFromClient = (item as any).message?.sender_type === 'client';
                        logger.debug(`🔍 Mídia ${item.id}:`, {
                          media: item,
                          message: (item as any).message,
                          sender_type: (item as any).message?.sender_type,
                          isFromClient,
                          messageText: (item as any).message?.message_text
                        });
                        return (
                          <MediaMessage
                            key={`media-${item.id}-${index}`}
                            media={item as ConversationMedia}
                            isFromClient={isFromClient}
                            messageText={(item as any).message?.message_text}
                            originalFileName={
                              (item as any).message?.metadata?.original_name as string | undefined
                            }
                          />
                        );
                      }
                    }).filter(Boolean); // Remove null values
                  })()}
                  {/* Elemento para scroll automático */}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Message Input */}
            <div className="bg-white border-t border-gray-200 p-4">
              {selectedConversation.status === 'finished' || selectedConversation.status === 'archived' ? (
                <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  Esta conversa está finalizada/arquivada e está em modo somente visualização.
                </div>
              ) : (
                <>

              {/* Indicador de imagem selecionada */}
              {selectedImage && (
                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <img
                        src={URL.createObjectURL(selectedImage)}
                        alt="Preview"
                        className="w-8 h-8 object-cover rounded"
                      />
                      <span className="text-sm text-blue-700">
                        Imagem selecionada: {selectedImage.name}
                      </span>
                    </div>
                    <button
                      onClick={() => setSelectedImage(null)}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Indicador de vídeo selecionado */}
              {selectedVideo && (
                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <video
                        src={URL.createObjectURL(selectedVideo)}
                        className="w-16 h-16 object-cover rounded"
                        muted
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-blue-700">
                          Vídeo selecionado
                        </span>
                        <span className="text-xs text-blue-600">
                          {selectedVideo.name} ({(selectedVideo.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedVideo(null)}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Indicador de documento selecionado */}
              {selectedDocument && (
                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Paperclip className="w-4 h-4 text-blue-600" />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-blue-700">
                          Documento selecionado
                        </span>
                        <span className="text-xs text-blue-600">
                          {selectedDocument.name} ({(selectedDocument.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedDocument(null)}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              <div
                className={`flex flex-col md:flex-row gap-2 rounded-lg transition-colors ${
                  isDragOverComposer ? 'ring-2 ring-blue-400 bg-blue-50/60 p-1' : ''
                }`}
                onDragOver={handleComposerDragOver}
                onDragLeave={handleComposerDragLeave}
                onDrop={handleComposerDrop}
              >
                <div className="flex items-center gap-2 overflow-x-auto md:overflow-visible pb-1 md:pb-0">
                  <button
                    onClick={() => document.getElementById('image-upload')?.click()}
                    disabled={isSending}
                    className={`p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      selectedImage 
                        ? 'text-blue-500 bg-blue-50' 
                        : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'
                    }`}
                    title="Selecionar imagem"
                  >
                    <ImageIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => document.getElementById('video-upload')?.click()}
                    disabled={isSending}
                    className={`p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      selectedVideo 
                        ? 'text-blue-500 bg-blue-50' 
                        : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'
                    }`}
                    title="Selecionar vídeo"
                  >
                    <Video className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => document.getElementById('document-upload')?.click()}
                    disabled={isSending}
                    className={`p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      selectedDocument
                        ? 'text-blue-500 bg-blue-50'
                        : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'
                    }`}
                    title="Selecionar documento"
                  >
                    <Paperclip className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickRepliesModalOpen(true)}
                    disabled={isSending}
                    className="p-2 rounded-lg text-gray-500 hover:text-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Inserir mensagem rápida"
                  >
                    <MessageCircle className="w-5 h-5" />
                  </button>
                </div>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageSelect(file);
                  }}
                  className="hidden"
                />
                <input
                  id="video-upload"
                  type="file"
                  accept="video/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleVideoSelect(file);
                  }}
                  className="hidden"
                />
                <input
                  id="document-upload"
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleDocumentSelect(file);
                  }}
                  className="hidden"
                />
                <div className="flex items-end gap-2 min-w-0 w-full">
                  <textarea
                    ref={messageTextareaRef}
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value)
                      adjustMessageTextareaHeight()
                    }}
                    placeholder={
                      selectedVideo 
                        ? "Digite uma mensagem para acompanhar o vídeo..." 
                        : selectedDocument
                          ? "Digite uma mensagem para acompanhar o documento..."
                        : selectedImage 
                          ? "Digite uma mensagem para acompanhar a imagem..." 
                          : "Digite sua mensagem..."
                    }
                    rows={1}
                    className="flex-1 min-w-0 min-h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    disabled={isSending}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={(!newMessage.trim() && !selectedImage && !selectedVideo && !selectedDocument) || isSending}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 flex-shrink-0"
                  >
                    {isSending ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
                </>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium mb-2">Selecione uma conversa</h3>
              <p>Escolha uma conversa da lista para começar a conversar</p>
            </div>
          </div>
        )}
      </div>
      {selectedConversation && (
        <ContactTagsWorkspaceModal
          isOpen={tagsWorkspaceOpen}
          onClose={() => setTagsWorkspaceOpen(false)}
          conversationId={selectedConversation.id}
          clientId={selectedConversation.client_id}
          onApplied={handleTagsApplied}
          onCatalogMutated={loadTagsCatalog}
          onOpenConversation={handleOpenConversationFromTagExplorer}
        />
      )}
      <QuickRepliesPickerModal
        isOpen={quickRepliesModalOpen}
        onClose={() => setQuickRepliesModalOpen(false)}
        onSelect={(content) => {
          if (!content) return
          setNewMessage((prev) => {
            if (!prev.trim()) return content
            return `${prev}\n${content}`
          })
        }}
        onOpenManager={() => setQuickRepliesManagerOpen(true)}
      />
      <QuickRepliesManagerModal
        isOpen={quickRepliesManagerOpen}
        onClose={() => setQuickRepliesManagerOpen(false)}
      />
    </div>
  )
}

export default Conversations